import React, { useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Edit, Trash2, ChevronDown, Plus } from 'lucide-react';
import { MenuCategory, MenuItem } from '../types';

interface SortableMenuItemProps {
  item: MenuItem;
  onEdit: (item: MenuItem) => void;
  onDelete: (itemId: string) => void;
  onToggleAvailability: (itemId: string, isAvailable: boolean) => void;
}

const SortableMenuItem: React.FC<SortableMenuItemProps> = ({
  item,
  onEdit,
  onDelete,
  onToggleAvailability,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="relative flex flex-col sm:flex-row items-start sm:items-center gap-2 p-2 sm:p-3 pl-10 bg-gray-50 border border-gray-200 rounded hover:shadow-sm transition-shadow ml-2 sm:ml-4 md:ml-8"
    >
      {/* Invisible handle on left so buttons remain clickable */}
      <div
        {...attributes}
        {...listeners}
        className="absolute inset-y-0 left-0 w-10 cursor-grab active:cursor-grabbing touch-none"
        aria-label="Drag to reorder item"
      />
      <div className="flex items-center gap-2 w-full sm:w-auto sm:flex-1">
        {/* Drag anywhere on the row; dedicated handle removed */}

        {item.imageUrl && (
          <img
            src={item.imageUrl}
            alt={item.name}
            loading="lazy"
            decoding="async"
            className="w-10 h-10 sm:w-12 sm:h-12 object-cover rounded flex-shrink-0"
          />
        )}

        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-gray-800 text-sm">{item.name}</h4>
          <p className="text-xs text-gray-500 line-clamp-1">{item.description}</p>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-sm font-bold text-red-600">
              £{item.price.toFixed(2)}
            </span>
            {item.extras && item.extras.length > 0 && (
              <span className="text-xs text-gray-500">
                +{item.extras.length} extras
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-1 w-full sm:w-auto justify-end">
        <button
          onClick={() => onToggleAvailability(item.id, !item.isAvailable)}
          className={`text-xs px-2 py-1 rounded whitespace-nowrap ${
            item.isAvailable
              ? 'bg-green-100 text-green-700'
              : 'bg-red-100 text-red-700'
          }`}
        >
          {item.isAvailable ? 'Available' : 'Unavailable'}
        </button>
        <button
          onClick={() => onEdit(item)}
          className="p-1.5 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded"
        >
          <Edit className="h-3.5 w-3.5" />
        </button>
        <button
          onClick={() => onDelete(item.id)}
          className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
};

interface CollapsibleCategoryProps {
  category: MenuCategory;
  items: MenuItem[];
  onEditCategory: (category: MenuCategory) => void;
  onDeleteCategory: (categoryId: string) => void;
  onReorderItems: (items: MenuItem[]) => void;
  onEditItem: (item: MenuItem) => void;
  onDeleteItem: (itemId: string) => void;
  onToggleAvailability: (itemId: string, isAvailable: boolean) => void;
  onAddItem: (categoryId: string) => void;
}

export const CollapsibleCategory: React.FC<CollapsibleCategoryProps> = ({
  category,
  items,
  onEditCategory,
  onDeleteCategory,
  onReorderItems,
  onEditItem,
  onDeleteItem,
  onToggleAvailability,
  onAddItem,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);

      const newOrder = arrayMove(items, oldIndex, newIndex);
      onReorderItems(newOrder);
    }
  };

  return (
    <div ref={setNodeRef} style={style} className="mb-2">
      {/* Category Header */}
      <div className="relative flex items-center gap-2 sm:gap-3 p-2 sm:p-4 pl-8 bg-white border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
        {/* Invisible drag handle area on the left */}
        <div
          {...attributes}
          {...listeners}
          className="absolute inset-y-0 left-0 w-8 cursor-grab active:cursor-grabbing touch-none"
          aria-label="Drag to reorder category"
        />

        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-1 sm:gap-2 flex-1 text-left min-w-0"
        >
          <ChevronDown
            className={`h-4 w-4 sm:h-5 sm:w-5 text-gray-500 transition-transform flex-shrink-0 ${
              isExpanded ? 'rotate-180' : ''
            }`}
          />
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-800 text-sm sm:text-base truncate">{category.name}</h3>
            {category.description && (
              <p className="text-xs sm:text-sm text-gray-500 line-clamp-1">{category.description}</p>
            )}
            <p className="text-xs text-gray-400 mt-1">
              {items.length} item{items.length !== 1 ? 's' : ''}
            </p>
          </div>
        </button>

        <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
          <button
            onClick={() => onAddItem(category.id)}
            className="p-1.5 sm:p-2 text-green-500 hover:text-green-700 hover:bg-green-50 rounded"
            title="Add Item"
          >
            <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
          </button>
          <button
            onClick={() => onEditCategory(category)}
            className="p-1.5 sm:p-2 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded"
          >
            <Edit className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
          </button>
          <button
            onClick={() => onDeleteCategory(category.id)}
            className="p-1.5 sm:p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
          >
            <Trash2 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
          </button>
        </div>
      </div>

      {/* Collapsible Items */}
      {isExpanded && (
        <div className="mt-2 space-y-2">
          {items.length === 0 ? (
            <div className="ml-2 sm:ml-4 md:ml-8 p-3 sm:p-4 text-center text-gray-500 text-sm">
              No items in this category. Click the + button above to add items.
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={items.map((item) => item.id)}
                strategy={verticalListSortingStrategy}
              >
                {items.map((item) => (
                  <SortableMenuItem
                    key={item.id}
                    item={item}
                    onEdit={onEditItem}
                    onDelete={onDeleteItem}
                    onToggleAvailability={onToggleAvailability}
                  />
                ))}
              </SortableContext>
            </DndContext>
          )}
        </div>
      )}
    </div>
  );
};

